import { formatAssetTimestamp } from "../../api/endpoints/accountAssets.js";
import {
  getAssetTypeAppMeta,
  getAssetTypeAssetMeta,
} from "./assetTypeUtils.js";

export const ASSET_FORM_EMPTY = {
  assetTypeId: "",
  values: {},
};

export function assetToForm(asset) {
  if (!asset) return { ...ASSET_FORM_EMPTY, values: {} };
  return {
    assetTypeId: asset.assetTypeId ?? "",
    values: { ...(asset.values ?? {}) },
  };
}

export function deriveAssetDisplayName(asset, assetType) {
  const appMeta = getAssetTypeAppMeta(assetType);
  const values = asset?.values ?? {};

  const model = String(values.model ?? "").trim();
  if (model) return model;

  const manufacturer = String(values.manufacturer ?? "").trim();
  const variant = String(values.variant ?? "").trim();
  if (manufacturer && variant) return `${manufacturer} ${variant}`;
  if (manufacturer) return manufacturer;

  const displayCol = appMeta.find((col) => col.key === "display_name") ?? appMeta[0];
  if (displayCol) {
    const value = String(values[displayCol.key] ?? "").trim();
    if (value) return value;
  }

  return asset?.name?.trim() || assetType?.name || "Asset";
}

export function assetFormToPayload(form, assetType) {
  const values = form.values ?? {};
  return {
    name: deriveAssetDisplayName({ values }, assetType),
    assetTypeId: form.assetTypeId.trim(),
    values,
  };
}

function validateMetaColumns(columns, values, valueErrors) {
  columns.forEach((col) => {
    const raw = values?.[col.key];
    if (!String(raw ?? "").trim()) {
      valueErrors[col.key] = `${formatColumnLabel(col.key)} is required`;
      return;
    }
    if (col.type === "int" && !/^-?\d+$/.test(String(raw).trim())) {
      valueErrors[col.key] = "Enter a whole number";
    }
  });
}

export function validateAssetStep(form, step, assetType) {
  const errors = {};
  if (step === 1) {
    if (!form.assetTypeId?.trim()) errors.assetTypeId = "Asset type is required";
    const valueErrors = {};
    validateMetaColumns(getAssetTypeAppMeta(assetType), form.values, valueErrors);
    if (Object.keys(valueErrors).length) errors.values = valueErrors;
    return errors;
  }

  const valueErrors = {};
  validateMetaColumns(getAssetTypeAssetMeta(assetType), form.values, valueErrors);
  if (Object.keys(valueErrors).length) errors.values = valueErrors;
  return errors;
}

export function formatColumnLabel(key) {
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export function buildAssetListSubtitle(asset, assetType) {
  const assetMetaValues = getAssetTypeAssetMeta(assetType)
    .map((col) => asset.values?.[col.key])
    .filter(Boolean);
  if (assetMetaValues.length > 0) return assetMetaValues.slice(0, 2).join(" · ");
  const appMetaValues = getAssetTypeAppMeta(assetType)
    .map((col) => asset.values?.[col.key])
    .filter(Boolean);
  if (appMetaValues.length > 0) return appMetaValues.slice(0, 2).join(" · ");
  return assetType?.name ?? "Asset";
}

export function buildAssetAppMetaFields(asset, assetType) {
  return getAssetTypeAppMeta(assetType).map((col) => ({
    label: formatColumnLabel(col.key),
    value: asset.values?.[col.key] ?? "—",
    icon: columnIcon(col.type),
  }));
}

export function buildAssetDetailFields(asset, assetType) {
  if (!asset) return [];
  const fields = [
    { label: "Asset type", value: assetType?.name ?? "—", icon: "layers" },
  ];
  getAssetTypeAssetMeta(assetType).forEach((col) => {
    fields.push({
      label: formatColumnLabel(col.key),
      value: asset.values?.[col.key] ?? "—",
      icon: columnIcon(col.type),
    });
  });
  fields.push(
    { label: "Created at", value: formatAssetTimestamp(asset.createdAt), icon: "device", wide: true },
    { label: "Updated at", value: formatAssetTimestamp(asset.updatedAt), icon: "device", wide: true },
  );
  return fields;
}

export function buildAssetDetailRows(fields) {
  const gridFields = fields.filter((f) => !f.wide);
  const wideFields = fields.filter((f) => f.wide);
  const rows = [];
  for (let i = 0; i < gridFields.length; i += 2) {
    rows.push(gridFields.slice(i, i + 2));
  }
  wideFields.forEach((field) => rows.push([field]));
  return rows;
}

function columnIcon(type) {
  if (type === "int") return "grid";
  if (type === "file") return "layers";
  return "model";
}
